import { sortByOrder } from '@scalar/helpers/array/sort-by-order';
import { objectKeys } from '@scalar/helpers/object/object-keys';
import { resolveOperationWithTraits } from '../../channel-example/resolve-operation-with-traits.js';
import { getNameFromRef } from '../../helpers/get-name-from-ref.js';
import { getResolvedAsyncApiRef } from '../../helpers/get-resolved-async-api-ref.js';
import { isHidden } from '../../helpers/is-hidden.js';
import { unpackProxyObject } from '../../helpers/unpack-proxy.js';
import { getNavigationOptions } from '../get-navigation-options.js';
import { getTag } from './get-tag.js';
import { getTagExternalDocs } from './get-tag-external-docs.js';
import { traverseDescription } from './traverse-description.js';
import { traverseSchemas } from './traverse-schemas.js';
/**
 * Picks the first display candidate that is non-empty after trimming.
 * Used by title helpers so we prefer `title`, then `summary`, then a fallback.
 */
const pickNonEmpty = (...candidates) => {
    for (const candidate of candidates) {
        const trimmed = candidate?.trim();
        if (trimmed) {
            return trimmed;
        }
    }
    return undefined;
};
/**
 * Returns the channel address shown in the sidebar and routing.
 * Falls back to the channel map key when `channel.address` is missing or empty.
 */
const getChannelAddress = (channelName, channel) => typeof channel.address === 'string' && channel.address.length > 0 ? channel.address : channelName;
/**
 * Sidebar label for a channel: `title`, then `summary`, then the resolved address.
 */
const getChannelTitle = (channel, channelName) => pickNonEmpty(channel.title, channel.summary) ?? getChannelAddress(channelName, channel);
/**
 * Sidebar label for an operation: `title`, then `summary`, then the operation map key.
 */
const getOperationTitle = (operation, operationName) => pickNonEmpty(operation.title, operation.summary) ?? operationName;
/**
 * Sidebar label for a message: `title`, then `summary`, then `name`, then the message map key.
 */
const getMessageTitle = (message, messageName) => pickNonEmpty(message.title, message.summary, message.name) ?? messageName;
/**
 * Parses a channel JSON pointer (`#/channels/<name>`) into the key used in `document.channels`.
 */
const getChannelNameFromRef = (ref) => getNameFromRef(ref, ['channels']);
/**
 * Resolves the channel referenced by an operation.
 *
 * The AsyncAPI 3 spec requires `operation.channel` to be a Reference Object,
 * so we read the channel key straight from the JSON pointer rather than trying
 * to match against the resolved channel by identity.
 */
const resolveOperationChannel = (document, operation) => {
    const channelRef = operation.channel;
    if (!channelRef || !('$ref' in channelRef)) {
        return undefined;
    }
    const channelName = getChannelNameFromRef(channelRef.$ref);
    if (!channelName) {
        return undefined;
    }
    const channelEntry = document.channels?.[channelName];
    if (!channelEntry) {
        return undefined;
    }
    const channel = getResolvedAsyncApiRef(channelEntry);
    return channel ? { channelName, channel } : undefined;
};
/**
 * Resolves which channel message names an operation references.
 *
 * - `operation.messages === undefined` → every channel message is included.
 * - `operation.messages === []` → only reply messages on the same channel are included.
 * - Otherwise → the channel messages that the operation or its reply explicitly references.
 */
const resolveOperationMessageNames = (operation, channel, channelName) => {
    const channelMessages = channel.messages ?? {};
    if (operation.messages === undefined) {
        return objectKeys(channelMessages);
    }
    const names = new Set();
    const replyMessages = getResolvedAsyncApiRef(operation.reply)?.messages ?? [];
    for (const messageRef of [...operation.messages, ...replyMessages]) {
        if (!('$ref' in messageRef)) {
            continue;
        }
        // Common case: a channel-scoped reference like `#/channels/<name>/messages/<id>`.
        const candidate = getNameFromRef(messageRef.$ref, ['channels', channelName, 'messages']);
        if (candidate && channelMessages[candidate]) {
            names.add(candidate);
            continue;
        }
        // Fallback: a channel-level message that points to the same target.
        for (const [name, entry] of Object.entries(channelMessages)) {
            if (entry && '$ref' in entry && entry.$ref === messageRef.$ref) {
                names.add(name);
            }
        }
    }
    return [...names];
};
/**
 * Normalizes an AsyncAPI tag (inline object or `$ref`) into the OpenAPI-style `TagObject`
 * used by the shared tag navigation helpers.
 */
const toTagObject = (tag) => {
    const resolved = getResolvedAsyncApiRef(tag);
    if (!resolved) {
        return [];
    }
    const externalDocs = getResolvedAsyncApiRef(resolved.externalDocs);
    return [
        {
            name: resolved.name,
            ...(resolved.description !== undefined ? { description: resolved.description } : {}),
            ...(externalDocs ? { externalDocs } : {}),
        },
    ];
};
/**
 * Sorts operation navigation entries in place using the workspace `operationsSorter` option.
 * Maps AsyncAPI `action` to the HTTP-style sorter fields so OpenAPI and AsyncAPI share the same API.
 */
const sortOperations = (entries, operationsSorter) => {
    if (operationsSorter === 'alpha') {
        entries.sort((a, b) => a.title.localeCompare(b.title));
        return;
    }
    if (operationsSorter === 'method') {
        entries.sort((a, b) => a.action.localeCompare(b.action));
        return;
    }
    if (typeof operationsSorter === 'function') {
        entries.sort((a, b) => operationsSorter({ method: a.action, path: a.operationName, ref: a.operationName, httpVerb: a.action }, { method: b.action, path: b.operationName, ref: b.operationName, httpVerb: b.action }));
    }
};
/**
 * Builds message entries beneath a channel or operation.
 * Resolves each message ref and skips hidden messages while preserving the declared order.
 */
const createMessageEntries = ({ channelName, channel, parentId, messageNames, generateId, }) => {
    const messages = messageNames.flatMap((messageName) => {
        const messageNode = channel.messages?.[messageName];
        if (!messageNode) {
            return [];
        }
        const message = getResolvedAsyncApiRef(messageNode);
        if (!message || isHidden(message)) {
            return [];
        }
        return [
            {
                type: 'asyncapi-message',
                id: generateId({
                    type: 'asyncapi-message',
                    messageName,
                    channelName,
                    parentId,
                }),
                title: getMessageTitle(message, messageName),
                messageName,
                channelName,
            },
        ];
    });
    return messages;
};
/**
 * Builds a single operation navigation entry under a channel.
 * Includes nested messages when the operation references channel messages (or all of them by default).
 * Returns `undefined` when the operation is hidden via `x-internal` or `x-scalar-ignore`.
 */
const createOperationEntry = ({ operationName, operation, channel, channelName, channelAddress, generateId, parentId, }) => {
    if (isHidden(operation)) {
        return undefined;
    }
    const operationId = generateId({
        type: 'asyncapi-operation',
        operationName,
        parentId,
    });
    const messageEntries = createMessageEntries({
        channelName,
        channel,
        parentId: operationId,
        messageNames: resolveOperationMessageNames(operation, channel, channelName),
        generateId,
    });
    return {
        type: 'asyncapi-operation',
        id: operationId,
        title: getOperationTitle(operation, operationName),
        operationName,
        action: operation.action,
        channelName,
        channelAddress,
        children: messageEntries.length > 0 ? messageEntries : undefined,
    };
};
/**
 * Builds a channel navigation entry from a bucket and the operations to render under it.
 * The same channel may appear at the document root and under each of its channel-level tags.
 * Writes `x-scalar-order` on the source channel (when it is not a bare `$ref`) so other code can reuse the order.
 * Returns `undefined` when the channel is hidden or all its declared operations are hidden.
 */
const createChannelEntry = ({ bucket, operationEntries, generateId, parentId, parentTag, operationsSorter, document, }) => {
    if (isHidden(bucket.channel)) {
        return undefined;
    }
    const channelId = generateId({
        type: 'asyncapi-channel',
        channelName: bucket.channelName,
        parentId,
        parentTag,
    });
    const operations = operationEntries.flatMap(({ operationName, operation }) => {
        const entry = createOperationEntry({
            operationName,
            operation,
            channel: bucket.channel,
            channelName: bucket.channelName,
            channelAddress: bucket.channelAddress,
            generateId,
            parentId: channelId,
        });
        return entry ? [entry] : [];
    });
    sortOperations(operations, operationsSorter);
    // A channel with hidden operations must not expose their messages through the fallback.
    if (operations.length === 0 && bucket.operations.length > 0) {
        return undefined;
    }
    // Keep channel-only messages reachable without duplicating messages already owned by an operation.
    const operationMessageNames = new Set(bucket.operations.flatMap(({ operation }) => resolveOperationMessageNames(operation, bucket.channel, bucket.channelName)));
    const messages = createMessageEntries({
        channelName: bucket.channelName,
        channel: bucket.channel,
        parentId: channelId,
        messageNames: objectKeys(bucket.channel.messages ?? {}).filter((name) => !operationMessageNames.has(name)),
        generateId,
    });
    const children = [...operations, ...messages];
    // Persist the rendered order back onto the source channel for downstream consumers.
    // We skip channels that are stored as references, since the order should live on the target.
    const channelNode = document.channels?.[bucket.channelName];
    if (channelNode && !('$ref' in channelNode)) {
        const orderedChannel = channelNode;
        orderedChannel['x-scalar-order'] = children.map((child) => child.id);
    }
    return {
        type: 'asyncapi-channel',
        id: channelId,
        title: getChannelTitle(bucket.channel, bucket.channelName),
        channelName: bucket.channelName,
        channelAddress: bucket.channelAddress,
        children,
    };
};
/**
 * Wraps collected channel entries in a tag navigation node.
 * Persists child order on the tag via `x-scalar-order` for later reordering in the UI.
 */
const createTagEntry = ({ tag, generateId, children, parentId, }) => {
    const id = generateId({
        type: 'tag',
        tag,
        parentId,
        isGroup: false,
    });
    const externalDocs = getTagExternalDocs(tag);
    const title = tag['x-displayName'] ?? tag.name ?? 'Untitled Tag';
    tag['x-scalar-order'] = children.map((child) => child.id);
    return {
        type: 'tag',
        id,
        title,
        name: tag.name || title,
        description: tag.description,
        ...(externalDocs ? { externalDocs } : {}),
        children,
        isGroup: false,
        isWebhooks: false,
    };
};
/**
 * Turns the in-memory `tagsMap` into top-level tag navigation entries.
 * Respects per-tag `x-scalar-order`, document-level `x-scalar-order`, and `tagsSorter` (alpha or custom).
 * Skips tags marked hidden.
 */
const getSortedTagEntries = ({ tagKeys, tagsMap, documentId, options: { tagsSorter, generateId }, sortOrder, }) => {
    const entries = tagKeys.flatMap((key) => {
        const { tag, entries: tagEntries } = getTag({ tagsMap, name: key, documentId, generateId });
        if (isHidden(tag)) {
            return [];
        }
        const tagOrder = tag['x-scalar-order'];
        return createTagEntry({
            tag,
            generateId,
            children: tagOrder ? sortByOrder(tagEntries, tagOrder, (item) => item.id) : tagEntries,
            parentId: documentId,
        });
    });
    if (sortOrder) {
        return sortByOrder(entries, sortOrder, (item) => item.id);
    }
    if (tagsSorter === 'alpha') {
        entries.sort((a, b) => a.title.localeCompare(b.title));
    }
    else if (typeof tagsSorter === 'function') {
        entries.sort((a, b) => tagsSorter(getTag({ tagsMap, name: a.name, documentId, generateId }).tag, getTag({ tagsMap, name: b.name, documentId, generateId }).tag));
    }
    return entries;
};
/**
 * Returns the existing bucket for a channel name or creates one with address and channel-level tags.
 * Buckets are shared across the traversal so operations on the same channel accumulate together.
 */
const getOrCreateChannelBucket = (channelBuckets, channelName, channel) => {
    const existing = channelBuckets.get(channelName);
    if (existing) {
        return existing;
    }
    const bucket = {
        channelName,
        channel,
        channelAddress: getChannelAddress(channelName, channel),
        channelTags: channel.tags?.flatMap(toTagObject) ?? [],
        operations: [],
    };
    channelBuckets.set(channelName, bucket);
    return bucket;
};
/**
 * Builds the channel/operation registry used to drive the rest of the traversal.
 *
 * Channels are seeded first so that they can appear in the sidebar even when no
 * operations reference them. Operations are then assigned to the bucket of the
 * channel they reference; operations with no resolvable channel are skipped.
 */
const collectChannelBuckets = (document) => {
    const channelBuckets = new Map();
    for (const [channelName, channelNode] of Object.entries(document.channels ?? {})) {
        if (!channelNode) {
            continue;
        }
        const channel = getResolvedAsyncApiRef(channelNode);
        if (!channel || isHidden(channel)) {
            continue;
        }
        getOrCreateChannelBucket(channelBuckets, channelName, channel);
    }
    for (const [operationName, operationNode] of Object.entries(document.operations ?? {})) {
        if (!operationNode) {
            continue;
        }
        const resolvedOperation = getResolvedAsyncApiRef(operationNode);
        if (!resolvedOperation) {
            continue;
        }
        const operation = resolveOperationWithTraits(resolvedOperation);
        const resolved = resolveOperationChannel(document, operation);
        if (!resolved) {
            continue;
        }
        const bucket = getOrCreateChannelBucket(channelBuckets, resolved.channelName, resolved.channel);
        // Operation-level tags are deliberately ignored here. Tag grouping is driven by
        // channel-level tags only until operation-tag grouping is reintroduced.
        bucket.operations.push({
            operationName,
            operation,
        });
    }
    return channelBuckets;
};
/**
 * Entry point: walks an AsyncAPI document and produces the sidebar tree.
 *
 * High-level flow:
 * 1. `collectChannelBuckets` — resolve refs, group operations by channel, collect channel tags.
 * 2. For each bucket — build the channel entry at the document root with all of its operations.
 * 3. Apply channel-level tags so a channel can also appear under each of its tags.
 * 4. Sort tags and top-level entries, then persist `x-scalar-order` on the document for stable ordering.
 *
 * Operation-level tags are intentionally not used to group channels yet; only channel-level
 * tags drive tag grouping.
 */
export const traverseAsyncApiDocument = (documentName, document, options) => {
    const { generateId, operationsSorter, tagsSorter, hideModels, modelsSectionLabel } = getNavigationOptions(documentName, options);
    const documentId = generateId({
        type: 'document',
        info: document.info,
        name: documentName,
    });
    const channelBuckets = collectChannelBuckets(document);
    const tagsMap = new Map();
    const untaggedChannels = [];
    // Info-level tags own their metadata and links, rather than contributing introduction links.
    for (const tag of (document.info.tags ?? []).flatMap(toTagObject)) {
        if (!tag.name) {
            continue;
        }
        const entry = getTag({ tagsMap, name: tag.name, documentId, generateId });
        entry.tag = tag;
    }
    for (const bucket of channelBuckets.values()) {
        // Operation-level tags are intentionally skipped for now: every operation is rendered
        // under its channel, and only the channel's own tags drive tag grouping below.
        if (bucket.channelTags.length === 0) {
            const untaggedChannel = createChannelEntry({
                bucket,
                operationEntries: bucket.operations,
                generateId,
                parentId: documentId,
                operationsSorter,
                document,
            });
            if (untaggedChannel) {
                untaggedChannels.push(untaggedChannel);
            }
        }
        // Channels can declare their own tags independently of any operation tags.
        for (const tag of bucket.channelTags) {
            const tagName = tag.name ?? 'Untitled Tag';
            const tagEntry = getTag({ tagsMap, name: tagName, documentId, generateId });
            tagEntry.tag = { ...tagEntry.tag, ...tag };
            const { id: tagId, entries } = tagEntry;
            const alreadyRegistered = entries.some((entry) => entry.type === 'asyncapi-channel' && entry.channelName === bucket.channelName);
            if (alreadyRegistered) {
                continue;
            }
            const taggedChannel = createChannelEntry({
                bucket,
                operationEntries: bucket.operations,
                generateId,
                parentId: tagId,
                parentTag: { tag, id: tagId },
                operationsSorter,
                document,
            });
            if (taggedChannel) {
                entries.push(taggedChannel);
            }
        }
    }
    // AsyncAPI documents carry reusable schemas under `components.schemas`, just like OpenAPI.
    // Reuse the OpenAPI schema traversal so they render as "Models" in the same way. Schemas
    // tagged via `x-tags` are pushed into `tagsMap` here, so this must run before the tag
    // entries below are built from that map.
    //
    // `traverseSchemas` only reads `components.schemas`, which both document shapes share, but it is
    // typed against the OpenAPI document. We hand it the resolved `components` (so a `$ref`-only
    // wrapper still exposes its schemas) wrapped in a minimal document; the cast bridges the single
    // overlapping field, since AsyncAPI and OpenAPI documents are otherwise distinct types.
    const components = document.components ? getResolvedAsyncApiRef(document.components) : undefined;
    const untaggedModels = !hideModels && components?.schemas
        ? traverseSchemas({
            documentId,
            document: {
                components: {
                    ...components,
                    schemas: Object.fromEntries(Object.entries(components.schemas).map(([name, schema]) => [name, getResolvedAsyncApiRef(schema)])),
                },
            },
            generateId,
            tagsMap,
        })
        : [];
    // Surface the Introduction entry plus any headings extracted from `info.description`
    // before the channel/tag entries, mirroring how OpenAPI documents are traversed.
    const entries = traverseDescription({
        generateId,
        parentId: documentId,
        info: document.info,
    });
    if (tagsMap.size > 0) {
        entries.push(...getSortedTagEntries({
            tagKeys: [...tagsMap.keys()],
            tagsMap,
            documentId,
            options: { tagsSorter, generateId },
            sortOrder: document['x-scalar-order'],
        }));
    }
    if (operationsSorter === 'alpha') {
        untaggedChannels.sort((a, b) => a.title.localeCompare(b.title));
    }
    entries.push(...untaggedChannels);
    // Mirror OpenAPI: untagged schemas are grouped under a single top-level "Models" section.
    if (untaggedModels.length) {
        entries.push({
            type: 'models',
            id: generateId({ type: 'model', parentId: documentId }),
            title: modelsSectionLabel,
            name: modelsSectionLabel,
            children: untaggedModels,
        });
    }
    const sortOrder = document['x-scalar-order'];
    const orderedEntries = sortOrder ? sortByOrder(entries, sortOrder, (entry) => entry.id) : entries;
    document['x-scalar-order'] = unpackProxyObject(orderedEntries.map((entry) => entry.id));
    return {
        id: documentId,
        type: 'document',
        title: document.info?.title?.trim() || 'Untitled Document',
        name: documentName,
        children: orderedEntries,
        icon: document['x-scalar-icon'],
    };
};
